import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  MousePointerClick,
  Users,
  Wallet,
  Target,
  TrendingUp,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { KPICard } from "@/components/nadia/KPICard";
import { DataBadge } from "@/components/nadia/Badges";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getDashboardSummary, getPlan } from "@/lib/api";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    Promise.all([getDashboardSummary(), getPlan().catch(() => null)])
      .then(([summary, savedPlan]) => { setData(summary); setPlan(savedPlan); })
      .catch(() => setData(null));
  }, []);

  if (!data) return <LoadingState />;

  const g = data.main_goal || {};
  const consultationGoal = g.source === "consultation";
  const decisions = data.chat_decisions || {};
  const approvedPlan = plan?.approved === true ? plan : null;
  const goal = [
    { value: savedText(approvedPlan?.commercial_goal), source: "Piano approvato" },
    { value: consultationGoal ? null : savedText(g.title), source: "Obiettivo salvato" },
    { value: savedText(decisions.goal?.value), source: "Dichiarato in chat" },
    { value: consultationGoal ? savedText(g.title) : null, source: "Priorità della consulenza" },
  ].find((item) => item.value) || { value: null, source: null };
  const budget = budgetSummary(approvedPlan?.budget_monthly, savedText(decisions.budget?.value));
  const country = savedText(decisions.country?.value);
  const priority = savedText(decisions.priority?.value);
  const samePriority = goal.value && priority && goal.value.trim().toLocaleLowerCase("it") === priority.trim().toLocaleLowerCase("it");
  const c = data.campaign || {};
  const m = c.metrics || {};
  const spend = finiteNumber(c.spend) ?? finiteNumber(m.spend);
  const conv = finiteNumber(m.conversions) ?? finiteNumber(m.results);
  const hasObservedMetrics = [m.spend, m.impressions, m.reach, m.clicks, m.leads, m.conversions, m.results]
    .some((value) => typeof value === "number" && Number.isFinite(value) && value > 0);
  const hasRealData = Boolean(c.id) && c.metrics_quality === "verificabile" && hasObservedMetrics;
  const kpiLabel = g.kpi_label || data.kpi_label || "Risultati";
  const goalState = goal.value
    ? goal.source === "Priorità della consulenza" ? "La priorità della consulenza è già disponibile nel riepilogo." : "L’obiettivo è già impostato nel riepilogo."
    : "L’obiettivo è da definire: possiamo sceglierlo insieme in chat.";
  const campaignState = c.status === "active"
    ? hasRealData ? "La campagna risulta attiva: trovi i risultati verificati nella sezione dedicata." : "La campagna risulta attiva; i risultati verificati non sono ancora disponibili."
    : c.id ? "Puoi rivedere lo stato della campagna nella pagina Campagne." : "Puoi preparare o rivedere una bozza in Campagne prima di autorizzare pubblicazioni o spese.";
  const insight = `${goalState} ${campaignState}`;

  return (
    <>
      <PageHeader
        eyebrow={`Ciao · ${data.business_name || "la tua attività"}`}
        title="Panoramica"
        subtitle={consultationGoal ? "La priorità della consulenza e il prossimo lavoro da approvare." : "Obiettivo, attività e risultati disponibili."}
        actions={
          <Link to="/app/nadia">
            <Button
              className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold"
              data-testid="open-nadia-chat-button"
            >
              <Sparkles size={16} className="mr-2" /> Parla con Nadia
            </Button>
          </Link>
        }
      />

      {/* Nadia insight card */}
      <div className="nadia-card p-5 sm:p-6 mb-6 relative overflow-hidden" data-testid="nadia-insight-card">
        <div
          className="absolute -top-24 -right-16 w-72 h-72 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(139,92,246,0.15), transparent 70%)" }}
        />
        <div className="flex items-start gap-4 relative">
          <NadiaAvatar size="md" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Nadia dice</div>
            </div>
            <p className="text-slate-200 text-sm sm:text-base leading-relaxed" data-testid="dashboard-insight">{insight}</p>
          </div>
        </div>
      </div>

      <section className="nadia-card p-5 sm:p-6 mb-6" aria-labelledby="overview-summary-title" data-testid="main-goal-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <h2 id="overview-summary-title" className="font-heading text-lg font-semibold text-slate-100" data-testid="overview-summary-title">Obiettivo e budget</h2>
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/app/obiettivo" className="text-cyan-300 hover:text-cyan-200 text-sm inline-flex items-center gap-1" data-testid="goal-details-link">Dettagli obiettivo <ArrowRight size={14} /></Link>
            <Link to="/app/nadia" className="text-sm text-cyan-300 hover:text-cyan-200 transition-colors" data-testid="chat-decisions-link">Continua in chat</Link>
          </div>
        </div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1" data-testid="main-goal-label">Obiettivo</p>
        <p className="font-heading text-2xl font-bold text-slate-50 break-words" data-testid="main-goal-title">{goal.value || "da definire"}</p>
        {goal.source && <p className="text-xs text-slate-400 mt-2" data-testid="overview-goal-source">Fonte: {goal.source}</p>}
        <dl className="mt-5 space-y-4">
          <div className="border-t border-white/10 pt-4">
            <dt className="text-xs font-semibold text-slate-400 mb-1" data-testid="overview-budget-label">Budget del piano approvato</dt>
            <dd className="text-sm text-slate-100 whitespace-pre-wrap break-words" data-testid="overview-budget-value">{budget}</dd>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-xs font-semibold text-slate-400 mb-1" data-testid="overview-country-label">Nazione</dt>
              <dd className="text-sm text-slate-100 break-words" data-testid="overview-country-value">{country || "da definire"}</dd>
            </div>
            {!samePriority && <div>
              <dt className="text-xs font-semibold text-slate-400 mb-1" data-testid="overview-priority-label">Priorità dichiarata in chat</dt>
              <dd className="text-sm text-slate-100 break-words" data-testid="overview-priority-value">{priority || "da definire"}</dd>
            </div>}
          </div>
        </dl>
        <p className="text-xs text-slate-400 mt-4" data-testid="overview-budget-note">Il budget approvato non è una spesa già sostenuta. Nessuna autorizzazione viene modificata da questo riepilogo.</p>
      </section>

      {/* Campaign results require explicit verification and observed activity. */}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-heading text-lg font-semibold text-slate-100" data-testid="campaign-results-title">Risultati campagna</h2>
        {hasRealData && <DataBadge variant="real" />}
      </div>
      {hasRealData ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard testId="kpi-spend" label="Spesa" value={spend !== null ? `€${spend}` : "da definire"} icon={Wallet} />
          <KPICard testId="kpi-clicks" label="Click" value={m.clicks ?? "da definire"} icon={MousePointerClick} />
          <KPICard testId="kpi-conversions" label={kpiLabel} value={conv ?? "da definire"} icon={Users} />
          <KPICard testId="kpi-cpa" label="Costo per risultato" value={finiteNumber(m.cpa) !== null ? `€${m.cpa.toFixed(2)}` : "da definire"} icon={Target} />
        </div>
      ) : (
        <div className="nadia-card p-5 mb-6 text-sm text-slate-400" data-testid="campaign-metrics-unavailable">
          Nessuna campagna con metriche verificate disponibili. I valori mancanti o tutti a zero non attestano risultati reali.
        </div>
      )}

      {/* Estimates */}
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-heading text-lg font-semibold text-slate-100">Previsione fine mese</h2>
        <DataBadge variant="estimate" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {hasRealData && c.status === "active" ? (<>
          <KPICard testId="kpi-est-conversions" variant="estimate" label={`${kpiLabel} stimati`} value={conv !== null ? `${Math.round(conv * 1.3)}–${Math.round(conv * 1.6)}` : "da definire"} icon={TrendingUp} helper="proiezione sul ritmo attuale" />
          <KPICard testId="kpi-est-budget" variant="estimate" label="Spesa prevista a fine mese" value={spend !== null ? `€${Math.round(spend * 1.5)}` : "da definire"} icon={Wallet} helper="proiezione" />
          <KPICard testId="kpi-est-roas" variant="estimate" label="Costo per risultato previsto" value={conv > 0 && spend !== null ? `€${(spend / conv).toFixed(2)}` : "da definire"} icon={TrendingUp} />
        </>) : (
          <div className="col-span-2 lg:col-span-3 nadia-card p-5 text-sm text-slate-400" data-testid="kpi-empty-forecast">Nessuna campagna attiva con dati reali: avvia la tua prima campagna e qui vedrai la previsione di fine mese basata sui risultati veri.</div>
        )}
      </div>

      {/* Pending actions bar */}
      {data.pending_actions > 0 && (
        <Link to="/app/azioni" className="block mt-8" data-testid="pending-actions-banner">
          <div className="nadia-card p-4 sm:p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <span className="inline-flex w-9 h-9 rounded-full bg-amber-500/10 border border-amber-500/25 items-center justify-center">
                <Sparkles size={16} className="text-amber-300" />
              </span>
              <div className="min-w-0">
                <div className="font-heading font-semibold text-slate-100 truncate">
                  {data.pending_actions} azioni in attesa della tua approvazione
                </div>
                <div className="text-xs text-slate-400">Nadia ha preparato modifiche per la campagna attiva</div>
              </div>
            </div>
            <ArrowRight size={18} className="text-slate-400 shrink-0" />
          </div>
        </Link>
      )}
    </>
  );
}

function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function savedText(value) {
  if (typeof value !== "string") return null;
  const text = value.trim();
  return !text || /^(?:da definire|da impostare(?: in chat)?|da confermare|—)$/i.test(text)
    ? null : text.replace(/\bda la tua zona\b/gi, "dalla tua zona");
}

function budgetSummary(amount, declared) {
  if (finiteNumber(amount) === null || amount < 0) return "da definire";
  const approved = `${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 2 }).format(amount)} € / mese`;
  if (!declared) return approved;
  const matches = [...declared.matchAll(/(?:€|EUR\b|euro\b)\s*(\d[\d.,]*)|(\d[\d.,]*)\s*(?:€|EUR\b|euro\b)/gi)];
  const token = matches.length === 1 ? (matches[0][1] || matches[0][2]) : declared.trim();
  const declaredAmount = /^\d+(?:[.,]\d{1,2})?$/.test(token) ? Number(token.replace(",", ".")) : null;
  const otherPeriod = /(?:giorn|settiman|annua|annuo|annuale|\banno\b|daily|weekly|yearly|annual)/i.test(declared);
  if (declaredAmount === amount && !otherPeriod) return approved;
  return `Dichiarato ${declared}, approvato ${approved}`;
}

function LoadingState() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-64 bg-white/5" />
      <Skeleton className="h-32 w-full bg-white/5" />
      <Skeleton className="h-40 w-full bg-white/5" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-28 bg-white/5" />
        ))}
      </div>
    </div>
  );
}
